import './lead-alerts.css';
import { useEffect, useRef, useState } from 'react';
import { useApiQuery } from '../../hooks.js';
import { queryString } from '../../lib/api-client.js';
import { number } from '../../format.js';
import { alertScope, createElectionVictoryTracker, createLeadChangeTracker, type LeadChange, type LeadSnapshot } from './lead-changes.js';
import { viewerCandidateColor, viewerUfNames } from './viewer-data.js';
import { createAutomaticElectionSound } from './automatic-sound.js';

const soundPath = '/static/audio/confirma-urna.mp3';
const formatPublication = new Intl.DateTimeFormat('pt-BR', { timeZone: 'America/Sao_Paulo', timeStyle: 'medium' });
type ElectionAlert = ((LeadChange & { kind: 'lead' }) | { kind: 'victory'; leader: LeadChange['leader']; publishedAt: number })
  & { key: string; turno: 1 | 2; location: string };

export function useElectionLeadAlerts(params: URLSearchParams, turno: 1 | 2) {
  const scope = alertScope(params, turno);
  const path = queryString({ cargo: scope.cargo, uf: scope.uf, municipio: scope.municipio, turno });
  // Reuse the same React Query entry as the displayed result, without a new
  // endpoint or writes. Background/historical samples never feed this observer.
  const result = useApiQuery<LeadSnapshot>(['apuracao', path], `/api/apuracao?${path}`, scope.enabled,
    { refetchInterval: turno === 2 ? 30_000 : 7_200_000 });
  const nationalPath = queryString({ cargo: 'presidente', uf: 'BR', turno });
  const national = useApiQuery<LeadSnapshot>(['apuracao', nationalPath], `/api/apuracao?${nationalPath}`, true,
    { refetchInterval: turno === 2 ? 30_000 : 7_200_000 });
  const tracker = useRef({ key: scope.key, value: createLeadChangeTracker() });
  const victoryTracker = useRef({ turno, value: createElectionVictoryTracker() });
  const sound = useRef<ReturnType<typeof createAutomaticElectionSound> | null>(null);
  const [soundError, setSoundError] = useState('');
  const [change, setChange] = useState<ElectionAlert | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const player = createAutomaticElectionSound({
      createContext: () => new AudioContext(),
      loadAudio: async () => {
        const response = await fetch(soundPath, { signal: controller.signal });
        if (!response.ok) throw new Error('Audio unavailable');
        return response.arrayBuffer();
      },
    });
    sound.current = player;
    const unlock = (event: Event) => { if (event.isTrusted) player.unlock(); };
    document.addEventListener('pointerup', unlock, { passive: true });
    document.addEventListener('keydown', unlock);
    return () => {
      document.removeEventListener('pointerup', unlock);
      document.removeEventListener('keydown', unlock);
      controller.abort(); player.dispose(); sound.current = null;
    };
  }, []);
  useEffect(() => {
    if (tracker.current.key !== scope.key) {
      tracker.current = { key: scope.key, value: createLeadChangeTracker() };
      setChange((current) => current?.kind === 'lead' ? null : current);
    }
    if (victoryTracker.current.turno !== turno) {
      victoryTracker.current = { turno, value: createElectionVictoryTracker() };
      setChange(null);
    }
    const victory = national.data && !national.isFetching && !national.isError && !national.isPlaceholderData
      ? victoryTracker.current.value.observe(national.data) : null;
    const snapshot = result.data;
    const next = scope.enabled && snapshot && !result.isFetching && !result.isPlaceholderData && !result.isError && snapshot.uf === scope.uf
      ? tracker.current.value.observe(snapshot) : null;
    if (!victory && !next) return;
    setSoundError('');
    if (victory) setChange({ kind: 'victory', leader: victory.winner, publishedAt: victory.publishedAt,
      key: `${turno}:presidente:BR:`, turno, location: `Presidente · Brasil · ${turno}º turno` });
    else if (next) {
      const territory = scope.uf === 'BR' ? 'Brasil' : viewerUfNames[scope.uf];
      const location = `${scope.cargo === 'presidente' ? 'Presidente' : 'Governador'} · ${snapshot?.municipality || territory} · ${turno}º turno`;
      setChange({ ...next, kind: 'lead', key: scope.key, turno, location });
    }
    // The final confirmation takes precedence over a simultaneous lead change:
    // one screen alert and one playback for this publication.
    void sound.current?.play().catch(() => setSoundError('O navegador bloqueou o áudio deste aviso.'));
  // Audio and tracker refs deliberately retain state between live snapshots.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data, result.isFetching, result.isPlaceholderData, result.isError, national.data, national.isFetching, national.isError, national.isPlaceholderData, scope.key, scope.enabled, scope.uf, scope.cargo, turno]);
  useEffect(() => {
    if (!change) return;
    const timer = window.setTimeout(() => setChange(null), 20_000);
    return () => window.clearTimeout(timer);
  }, [change]);

  return {
    notification: change && change.turno === turno && (change.kind === 'victory' || change.key === scope.key) ? <aside className="election-lead-alert" role="alert" aria-atomic="true" style={{ borderTopColor: viewerCandidateColor(change.leader) }}>
      <button className="election-lead-close" type="button" aria-label="Fechar alerta da apuração" onClick={() => setChange(null)}>×</button>
      <span className="election-lead-eyebrow">{change.kind === 'victory' ? '100% das seções · resultado confirmado' : 'Virada na liderança · ao vivo'}</span>
      <strong>{change.leader.name} {change.kind === 'victory' ? 'eleito presidente' : 'assumiu a liderança'}</strong>
      <p>{change.kind === 'victory' ? 'Todas as seções foram totalizadas e o TSE confirmou a candidatura eleita.' : `Ultrapassou ${change.previous.name} nesta apuração.`}</p>
      <small>{change.location}</small>
      <div className="election-lead-result"><b>{number(change.leader.votes)} votos</b><span>Publicação TSE às {formatPublication.format(new Date(change.publishedAt))}</span></div>
      {soundError && <small className="election-lead-audio-note">{soundError}</small>}
    </aside> : null,
  };
}
